/**
 * Hotel Update Price API
 *
 * Handles hotel price update API calls (PAN/TCS updates).
 */
import type {
  HotelReviewPrice,
  FareBreakupData,
  HotelUpdatePriceCBPayload,
  HotelUpdatePriceRequestBody,
} from '@contracts';
import { HOTELS_ENDPOINTS } from '@/api/endpoints';
import { logger } from '@/shared/utils/logger';
import { baseFetch, createApiError, buildCBUrlParamsFull, buildCBHeaders } from './baseApi';

// ============================================
// Error Class
// ============================================

export const HotelUpdatePriceError = createApiError('HotelUpdatePriceError');

// ============================================
// Request Types
// ============================================

export interface HotelUpdatePriceRequestParams {
  sessionId: string;
  userId: string;
  body: HotelUpdatePriceRequestBody;
}

// Re-export for backward compatibility
export type UpdatePriceRequestBody = HotelUpdatePriceRequestBody;

// ============================================
// Response Types
// ============================================

export interface UpdatePriceSummaryData {
  reviewPrice: HotelReviewPrice | null;
  fareBreakup: FareBreakupData | null;
}

export interface HotelUpdatePriceResponse {
  type: 'hotels-update-price';
  success: boolean;
  priceSummary: UpdatePriceSummaryData | null;
  statusMessage: string | null;
  ratePlanList: unknown[];
  error?: { code: string; message: string };
}

// ============================================
// Raw API Response Type
// ============================================

interface RawResponse {
  success?: boolean;
  priceSummary?: { reviewPrice?: HotelReviewPrice; fareBreakup?: FareBreakupData } | null;
  statusMessage?: string | null;
  ratePlanList?: unknown[];
  error?: { code: string; message: string };
}

// ============================================
// Helper
// ============================================

function transformPriceSummary(raw: RawResponse['priceSummary']): UpdatePriceSummaryData | null {
  if (!raw) return null;
  return {
    reviewPrice: raw.reviewPrice ?? null,
    fareBreakup: raw.fareBreakup ?? null,
  };
}

// ============================================
// Helper Functions
// ============================================

/**
 * Build the CB backend payload with urlParams, headers, and requestBody
 * Uses common builders from baseApi
 */
export function buildCBPayload(
  requestBodyInput: HotelUpdatePriceRequestBody,
): HotelUpdatePriceCBPayload {
  return {
    urlParams: buildCBUrlParamsFull(),
    headers: buildCBHeaders(),
    requestBody: requestBodyInput,
  };
}

// ============================================
// API Function
// ============================================

/**
 * Update price with PAN/TCS details
 * POST /hotels/update-price?sessionId=...&userId=...
 */
export async function fetchHotelUpdatePrice(
  params: HotelUpdatePriceRequestParams,
): Promise<HotelUpdatePriceResponse> {
  const payload = buildCBPayload(params.body);

  const { data, error } = await baseFetch<RawResponse>({
    endpoint: HOTELS_ENDPOINTS.UPDATE_PRICE,
    queryParams: { sessionId: params.sessionId, userId: params.userId },
    body: payload,
    logTag: 'HotelUpdatePriceClient',
  });

  // Network/HTTP error
  if (error) {
    return {
      type: 'hotels-update-price',
      success: false,
      priceSummary: null,
      statusMessage: null,
      ratePlanList: [],
      error,
    };
  }

  const priceSummary = transformPriceSummary(data?.priceSummary);
  const statusMessage = data?.statusMessage ?? null;
  const ratePlanList = data?.ratePlanList ?? [];

  // API returned error
  if (data?.success === false && data?.error) {
    logger.warn('[HotelUpdatePriceClient] API returned error:', data.error);
    return {
      type: 'hotels-update-price',
      success: false,
      priceSummary,
      statusMessage,
      ratePlanList,
      error: data.error,
    };
  }

  // Success
  logger.info('[HotelUpdatePriceClient] Success:', {
    statusMessage,
    hasPriceSummary: priceSummary !== null,
  });
  return {
    type: 'hotels-update-price',
    success: true,
    priceSummary,
    statusMessage,
    ratePlanList,
  };
}
